import { Component } from "@theme/component";

/**
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video - The video element.
 * @property {HTMLSourceElement[]} sources - The source elements.
 * @property {HTMLButtonElement} [muteButton] - Optional mute toggle button.
 */

/**
 * A lazy-loading video component that only loads the video when it enters the viewport.
 * Uses IntersectionObserver for efficient viewport detection.
 * Add the `eager` attribute to load immediately (for above-the-fold videos).
 *
 * @extends Component<Refs>
 */
class LazyVideoComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {MediaQueryList | null} */
  #posterMediaQuery = null;

  /** @type {(() => void) | null} */
  #posterMediaQueryHandler = null;

  /** @type {boolean} */
  #loaded = false;

  /** @type {(() => void) | null} */
  #autoplayRetryCleanup = null;

  /** @type {number | null} */
  #playbackStartTimeout = null;

  /** @type {number | null} */
  #fallbackRevealTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupPosterSwitching();
    this.#setupMuteButton();

    if (this.hasAttribute("eager")) {
      this.#loadVideo();
    } else {
      this.#setupObserver();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroyObserver();
    this.#destroyPosterSwitching();
    this.#clearPlaybackRecovery();
    this.#destroyMuteButton();
  }

  /**
   * Sets up the IntersectionObserver to detect when the component enters the viewport.
   */
  #setupObserver() {
    if (this.#loaded) return;

    const video = this.refs.video;
    if (!video) return;

    const options = {
      root: null,
      rootMargin: "100px",
      threshold: 0,
    };

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !this.#loaded) {
          this.#loadVideo();
          this.#destroyObserver();
        }
      }
    }, options);

    this.#observer.observe(this);
  }

  /**
   * Destroys the IntersectionObserver.
   */
  #destroyObserver() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  /**
   * Applies the right poster image based on breakpoint.
   * Uses optional data attributes:
   * - data-poster-desktop
   * - data-poster-mobile
   */
  #applyPosterSource() {
    const video = this.refs.video;
    if (!video) return;

    const posterDesktop = video.dataset.posterDesktop;
    const posterMobile = video.dataset.posterMobile;

    if (!posterDesktop && !posterMobile) return;

    const useMobilePoster = Boolean(
      posterMobile && this.#posterMediaQuery?.matches,
    );
    const nextPoster = useMobilePoster
      ? posterMobile
      : (posterDesktop ?? posterMobile);

    if (nextPoster && video.poster !== nextPoster) {
      video.poster = nextPoster;
    }
  }

  /**
   * Sets up poster switching on viewport changes when data-poster-mobile is provided.
   */
  #setupPosterSwitching() {
    const video = this.refs.video;
    if (!video) return;

    this.#destroyPosterSwitching();

    if (video.dataset.posterMobile) {
      this.#posterMediaQuery = window.matchMedia("(max-width: 749px)");
      this.#posterMediaQueryHandler = () => {
        this.#applyPosterSource();
      };

      if (typeof this.#posterMediaQuery.addEventListener === "function") {
        this.#posterMediaQuery.addEventListener(
          "change",
          this.#posterMediaQueryHandler,
        );
      } else if (typeof this.#posterMediaQuery.addListener === "function") {
        this.#posterMediaQuery.addListener(this.#posterMediaQueryHandler);
      }
    }

    this.#applyPosterSource();
  }

  /**
   * Removes poster switching listeners.
   */
  #destroyPosterSwitching() {
    if (!this.#posterMediaQuery || !this.#posterMediaQueryHandler) return;

    if (typeof this.#posterMediaQuery.removeEventListener === "function") {
      this.#posterMediaQuery.removeEventListener(
        "change",
        this.#posterMediaQueryHandler,
      );
    } else if (typeof this.#posterMediaQuery.removeListener === "function") {
      this.#posterMediaQuery.removeListener(this.#posterMediaQueryHandler);
    }

    this.#posterMediaQuery = null;
    this.#posterMediaQueryHandler = null;
  }

  /** @type {(() => void) | null} */
  #muteButtonHandler = null;

  /**
   * Sets up the mute toggle button if present.
   */
  #setupMuteButton() {
    const button = this.refs.muteButton;
    if (!button) return;

    this.#muteButtonHandler = () => {
      const video = this.refs.video;
      if (!video) return;

      video.muted = !video.muted;
      button.setAttribute("aria-pressed", String(!video.muted));

      const label = video.muted
        ? button.dataset.labelUnmute
        : button.dataset.labelMute;
      if (label) {
        button.setAttribute("aria-label", label);
      }
    };

    button.addEventListener("click", this.#muteButtonHandler);
  }

  /**
   * Removes the mute button listener.
   */
  #destroyMuteButton() {
    const button = this.refs.muteButton;
    if (button && this.#muteButtonHandler) {
      button.removeEventListener("click", this.#muteButtonHandler);
    }
    this.#muteButtonHandler = null;
  }

  #clearPlaybackRecovery() {
    if (this.#autoplayRetryCleanup) {
      this.#autoplayRetryCleanup();
      this.#autoplayRetryCleanup = null;
    }

    if (this.#playbackStartTimeout !== null) {
      window.clearTimeout(this.#playbackStartTimeout);
      this.#playbackStartTimeout = null;
    }

    if (this.#fallbackRevealTimeout !== null) {
      window.clearTimeout(this.#fallbackRevealTimeout);
      this.#fallbackRevealTimeout = null;
    }
  }

  /**
   * Returns true when a poster or image fallback exists.
   *
   * @param {HTMLVideoElement} video
   */
  #hasPosterFallback(video) {
    if (
      video.poster ||
      video.dataset.posterDesktop ||
      video.dataset.posterMobile
    ) {
      return true;
    }

    const imageFallback = this.querySelector("picture, img");
    return imageFallback instanceof HTMLElement;
  }

  /**
   * Schedules a replay attempt for browsers that reject autoplay until
   * the tab is visible or after a user gesture.
   *
   * @param {() => void} retryPlayback
   * @param {() => void} revealVideo
   * @param {HTMLVideoElement} video
   */
  #scheduleAutoplayRetry(retryPlayback, revealVideo, video) {
    if (this.#autoplayRetryCleanup) return;

    const retryOnInteraction = () => {
      if (document.visibilityState === "hidden") return;
      this.#clearPlaybackRecovery();
      retryPlayback();
    };

    const retryOnVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        this.#clearPlaybackRecovery();
        retryPlayback();
      }
    };

    window.addEventListener("pointerdown", retryOnInteraction, {
      once: true,
      passive: true,
    });
    window.addEventListener("touchstart", retryOnInteraction, {
      once: true,
      passive: true,
    });
    window.addEventListener("click", retryOnInteraction, { once: true });
    window.addEventListener("keydown", retryOnInteraction, { once: true });
    document.addEventListener("visibilitychange", retryOnVisibilityChange);

    this.#autoplayRetryCleanup = () => {
      window.removeEventListener("pointerdown", retryOnInteraction);
      window.removeEventListener("touchstart", retryOnInteraction);
      window.removeEventListener("click", retryOnInteraction);
      window.removeEventListener("keydown", retryOnInteraction);
      document.removeEventListener("visibilitychange", retryOnVisibilityChange);
    };

    if (
      !this.#hasPosterFallback(video) &&
      this.#fallbackRevealTimeout === null
    ) {
      this.#fallbackRevealTimeout = window.setTimeout(() => {
        this.#fallbackRevealTimeout = null;
        revealVideo();
      }, 1200);
    }
  }

  /**
   * Loads and plays the video.
   * Swaps data-src to src if needed (for lazy mode), or uses existing src (for eager mode).
   */
  #loadVideo() {
    const video = this.refs.video;
    if (!video || this.#loaded) return;

    this.#loaded = true;
    this.#clearPlaybackRecovery();

    video.autoplay = true;
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute("autoplay", "");
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");

    const sources = Array.isArray(this.refs.sources) ? this.refs.sources : [];

    // Swap data-src to src if sources use lazy loading pattern
    let needsLoad = false;
    if (sources.length > 0) {
      for (const source of sources) {
        const dataSrc = source.getAttribute("data-src");
        if (dataSrc) {
          source.setAttribute("src", dataSrc);
          needsLoad = true;
        }
      }
    }

    if (needsLoad) {
      video.load();
    }

    let revealed = false;
    const revealVideo = () => {
      if (revealed) return;
      revealed = true;
      this.#clearPlaybackRecovery();
      this.classList.add("lazy-video--loaded");
    };

    const revealOnRenderedFrame = () => {
      if (typeof video.requestVideoFrameCallback === "function") {
        video.requestVideoFrameCallback(() => {
          revealVideo();
        });
        return;
      }

      if (video.currentTime > 0) {
        requestAnimationFrame(() => {
          revealVideo();
        });
        return;
      }

      video.addEventListener("timeupdate", revealVideo, { once: true });
    };

    const startPlayback = () => {
      if (video.paused === false) {
        revealOnRenderedFrame();
        return;
      }

      const playPromise = video.play();

      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            revealOnRenderedFrame();
          })
          .catch(() => {
            this.#scheduleAutoplayRetry(startPlayback, revealVideo, video);
          });
      } else {
        revealOnRenderedFrame();
      }
    };

    const queuePlaybackStart = () => {
      if (this.#playbackStartTimeout !== null) {
        window.clearTimeout(this.#playbackStartTimeout);
        this.#playbackStartTimeout = null;
      }
      startPlayback();
    };

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      startPlayback();
    } else {
      video.addEventListener("loadeddata", queuePlaybackStart, { once: true });
      video.addEventListener("canplay", queuePlaybackStart, { once: true });
      this.#playbackStartTimeout = window.setTimeout(queuePlaybackStart, 1500);
    }
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#setupPosterSwitching();
    this.#destroyMuteButton();
    this.#setupMuteButton();

    if (!this.#loaded) {
      if (this.hasAttribute("eager")) {
        this.#loadVideo();
      } else {
        this.#setupObserver();
      }
    }
  }
}

if (!customElements.get("lazy-video")) {
  customElements.define("lazy-video", LazyVideoComponent);
}
